import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUsersWithPermissions, UserWithPermissions, removeUserPermissions, getAdminRoles } from '@/lib/firebase';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Shield, Users, Edit2, Trash2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import AddUserModal from '@/components/add-user-modal';
import EditUserModal from '@/components/edit-user-modal';
import { captureHandledException } from '@/lib/sentry';

interface AdminRole {
  id: string;
  roleName: string;
}

export default function UserManagement() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editingUser, setEditingUser] = useState<UserWithPermissions | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [removingUser, setRemovingUser] = useState<string | null>(null);
  const [adminRoles, setAdminRoles] = useState<AdminRole[]>([]);

  useEffect(() => {
    getAdminRoles()
      .then(roles => setAdminRoles(roles.sort((a, b) => (a.roleName ?? '').localeCompare(b.roleName ?? '')) as AdminRole[]))
      .catch((error) => {
        captureHandledException(error, "user-management.load-admin-roles");
      });
  }, []);

  const { data: usersWithPermissions, isLoading, error } = useQuery({
    queryKey: ['/api/users-with-permissions'],
    queryFn: getUsersWithPermissions,
  });

  const getRoleName = (roleId: string) => {
    const role = adminRoles.find(r => r.id === roleId);
    return role?.roleName || roleId;
  };

  const handleUserAdded = () => queryClient.invalidateQueries({ queryKey: ['/api/users-with-permissions'] });
  const handleUserUpdated = () => queryClient.invalidateQueries({ queryKey: ['/api/users-with-permissions'] });

  const handleEditUser = (user: UserWithPermissions) => {
    setEditingUser(user);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingUser(null);
  };

  const handleRemoveUser = async (user: UserWithPermissions) => {
    setRemovingUser(user.id);
    try {
      await removeUserPermissions(user.id);
      toast({ title: "User Removed", description: `${user.email} has been removed from the system.` });
      queryClient.invalidateQueries({ queryKey: ['/api/users-with-permissions'] });
    } catch (error) {
      captureHandledException(error, "user-management.remove-user");
      toast({ title: "Failed to Remove User", description: "There was an error removing the user. Please try again.", variant: "destructive" });
    } finally {
      setRemovingUser(null);
    }
  };

  return (
    <div className="flex-1 p-8">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="h-6 w-6" />
              User Management
            </h1>
            <p className="text-slate-600 mt-1">Manage users and their roles</p>
          </div>
          <AddUserModal onUserAdded={handleUserAdded} />
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-4 flex-1" />
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-20" />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="p-8 text-center">
                <Shield className="h-10 w-10 text-red-400 mx-auto mb-3" />
                <p className="text-red-600 font-medium">Failed to load users</p>
                <p className="text-slate-500 text-sm mt-1">Check your connection and try again.</p>
              </div>
            ) : !usersWithPermissions || usersWithPermissions.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">No users found</p>
                <p className="text-slate-400 text-sm mt-1">Add a user to get started.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usersWithPermissions.map(user => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.email}</TableCell>
                      <TableCell>
                        {user.permissions.length > 0 ? (
                          <Badge variant="secondary">{getRoleName(user.permissions[0])}</Badge>
                        ) : (
                          <span className="text-slate-400 text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                disabled={removingUser === user.id}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove User</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to remove {user.email}? This will delete all adminUsers records for this user and they will lose access to the system.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleRemoveUser(user)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <EditUserModal
          user={editingUser}
          open={editModalOpen}
          onClose={handleCloseEditModal}
          onUserUpdated={handleUserUpdated}
        />
      </div>
    </div>
  );
}
